/** Convert clicks on a chart image through user-marked axis ticks.
 *
 * The mapping is piecewise between adjacent ticks. It therefore works for
 * linear, logarithmic, and category-spaced axes without guessing the scale.
 * Coordinates are fractions of the displayed image, independent of zoom.
 */
export function parseTickValues(value) {
  const parts = String(value || "").trim().split(/[;\s,]+/).filter(Boolean);
  if (parts.length < 2) return null;
  const numbers = parts.map(Number);
  if (numbers.some((number) => !Number.isFinite(number))) return null;
  return numbers;
}

export function validAnchors(anchors) {
  if (!Array.isArray(anchors) || anchors.length < 2) return false;
  const sorted = [...anchors].sort((a, b) => a.pixel - b.pixel);
  if (sorted.some((a) => !Number.isFinite(a.pixel) || !Number.isFinite(a.value))) return false;
  const direction = Math.sign(sorted[1].value - sorted[0].value);
  if (!direction) return false;
  return sorted.every((anchor, index) => index === 0 || (
    anchor.pixel - sorted[index - 1].pixel > 0.0001 &&
    Math.sign(anchor.value - sorted[index - 1].value) === direction
  ));
}

export function valueAtPixel(anchors, pixel, snapTolerance = 0) {
  if (!validAnchors(anchors) || !Number.isFinite(pixel)) return null;
  const sorted = [...anchors].sort((a, b) => a.pixel - b.pixel);
  const near = sorted.find((anchor) => Math.abs(pixel - anchor.pixel) <= snapTolerance);
  if (near) return near.value;
  if (pixel < sorted[0].pixel || pixel > sorted[sorted.length - 1].pixel) return null;
  for (let index = 1; index < sorted.length; index += 1) {
    const right = sorted[index];
    if (pixel <= right.pixel) {
      const left = sorted[index - 1];
      return left.value + (pixel - left.pixel) / (right.pixel - left.pixel) * (right.value - left.value);
    }
  }
  return null;
}

export function chartPointAtPixel(xAnchors, yAnchors, x, y) {
  const load = valueAtPixel(xAnchors, x);
  const efficiency = valueAtPixel(yAnchors, y);
  if (load == null || efficiency == null || load <= 0 || load > 100 ||
      efficiency <= 0 || efficiency > 100) return null;
  return {
    // Keep capture precision; nearby clicks must not collapse to one load.
    load: Number(load.toFixed(6)),
    efficiency: Number(efficiency.toFixed(6)),
    x,
    y,
  };
}
